/**
 * Disabilities List Page
 * List and manage Disabilities (Deficiências PCD)
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Assessment as StatsIcon,
  Cancel as InactiveIcon,
  CheckCircle as ActiveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Warning as UrgentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  ActionButton,
  PageShell,
  DataTable,
  type DataTableColumn,
  RouteActionButton,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { disabilityService } from '../../services/disabilityService';
import { openSignedFile } from '../../services/fileAccess';
import {
  Disability,
  DisabilityFilters,
  DisabilityStatistics,
  formatDate,
  getPriorityColor,
  getSeverityColor,
} from '../../types/disability';
import { DisabilitiesFiltersComponent, DisabilitiesImportDialog } from './components';
import type { DisabilitiesFiltersData } from './components/DisabilitiesFilters';

const columns: DataTableColumn<Disability>[] = [
  {
    id: 'employeeName',
    label: 'Funcionário',
    primary: true,
    render: (disability) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2">{disability.employeeName}</Typography>
        {disability.needsUrgentAccommodation && (
          <Tooltip title="Necessita acomodação urgente">
            <UrgentIcon color="error" fontSize="small" />
          </Tooltip>
        )}
      </Box>
    ),
  },
  { id: 'companyName', label: 'Empresa', render: (disability) => disability.companyName },
  {
    id: 'disabilityType',
    label: 'Tipo',
    render: (disability) => (
      <Chip label={disability.disabilityType} size="small" variant="outlined" />
    ),
  },
  {
    id: 'severity',
    label: 'Severidade',
    render: (disability) => (
      <Chip
        label={disability.severity}
        size="small"
        color={getSeverityColor(disability.severity)}
      />
    ),
  },
  {
    id: 'priority',
    label: 'Prioridade',
    render: (disability) => (
      <Chip
        label={disability.priority}
        size="small"
        color={getPriorityColor(disability.priority)}
      />
    ),
  },
  {
    id: 'registrationDate',
    label: 'Data de Registro',
    render: (disability) => formatDate(disability.registrationDate),
  },
  {
    id: 'isActive',
    label: 'Status',
    render: (disability) => (
      <Tooltip title={disability.isActive ? 'Ativo' : 'Inativo'}>
        {disability.isActive ? <ActiveIcon color="success" /> : <InactiveIcon color="error" />}
      </Tooltip>
    ),
  },
];

export const DisabilitiesListPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const editar = useControle('/disabilities/:id/edit', pode('disability.update'), 'Editar');
  const gerarLaudo = useAcao('disability.read', 'Gerar Laudo Caracterizador');
  const excluir = useAcao('disability.delete', 'Excluir');
  const [disabilities, setDisabilities] = useState<Disability[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<DisabilityFilters>({});
  const [statistics, setStatistics] = useState<DisabilityStatistics | null>(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [disabilityToDelete, setDisabilityToDelete] = useState<Disability | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Import/Export state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const loadDisabilities = async (
    pageNumber: number = page,
    limit: number = rowsPerPage,
    filterOverride: DisabilityFilters = filters,
  ) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pageNumber + 1,
        limit,
        ...filterOverride,
      };

      const response = await disabilityService.getDisabilities(params);

      setDisabilities(response.disabilities);
      setTotal(response.total);
    } catch (err: unknown) {
      console.error('Error loading disabilities:', err);
      setError((err as Error).message ?? 'Erro ao carregar deficiências');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await disabilityService.getStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  };

  useEffect(() => {
    loadDisabilities();
    loadStatistics();
  }, [page, rowsPerPage]);

  const handleFilter = (data: DisabilitiesFiltersData) => {
    const apiFilters: DisabilityFilters = {
      search: data.search || undefined,
      disabilityType: data.disabilityType || undefined,
      severity: data.severity || undefined,
      isActive: data.isActive === '' ? undefined : data.isActive === 'true',
    };

    setFilters(apiFilters);
    setPage(0);
    loadDisabilities(0, rowsPerPage, apiFilters);
  };

  const handleDeleteClick = (id: number) => {
    const disability = disabilities.find(d => d.id === id);
    if (disability) {
      setDisabilityToDelete(disability);
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!disabilityToDelete) return;

    setDeleting(true);
    try {
      await disabilityService.deleteDisability(disabilityToDelete.id);
      setDeleteDialogOpen(false);
      setDisabilityToDelete(null);
      loadDisabilities();
      loadStatistics();
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Erro ao excluir deficiência');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDisabilityToDelete(null);
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const blob = await disabilityService.exportDisabilities();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      link.download = `deficiencias-export-${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Erro ao exportar deficiências');
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportComplete = () => {
    loadDisabilities();
    loadStatistics();
  };

  const handleEdit = (disability: Disability) => {
    navigate(`/disabilities/${disability.id}/edit`);
  };

  const handleGenerateLaudo = async (d: Disability) => {
    const target = window.open('about:blank', '_blank');
    if (!target) {
      enqueueSnackbar('Permita popups para visualizar o PDF', { variant: 'warning' });
      return;
    }
    try {
      const { pdfPath } = await disabilityService.generateLaudoPdf(d.id);
      await openSignedFile(pdfPath, target);
    } catch (err: unknown) {
      target.close();
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao gerar laudo', { variant: 'error' });
    }
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Deficiências PCD' },
      ]}
      title="Deficiências PCD"
      subtitle="Gerencie os registros de pessoas com deficiência"
      actions={
        <>
          <ActionButton
            acao="disability.export"
            rotulo="Exportar"
            variant="outlined"
            startIcon={exportLoading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
            onClick={handleExport}
            disabled={exportLoading}
          >
            Exportar
          </ActionButton>
          <ActionButton
            acao="disability.import"
            rotulo="Importar"
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            Importar
          </ActionButton>
          <RouteActionButton
            para="/disabilities/new"
            exige={pode('disability.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Registrar Deficiência
          </RouteActionButton>
        </>
      }
    >
      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Total de Registros
              </Typography>
              <Typography variant="h4">{statistics.totalDisabilities}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Funcionários PCD
              </Typography>
              <Typography variant="h4">{statistics.totalEmployees}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Com Acomodações
              </Typography>
              <Typography variant="h4" color="success.main">
                {statistics.withAccommodations}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Sem Acomodações
              </Typography>
              <Typography variant="h4" color="warning.main">
                {statistics.withoutAccommodations}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <DisabilitiesFiltersComponent
          onFilter={handleFilter}
          loading={loading}
          total={total}
        />
      </Box>

      {/* Table */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Chip
          icon={<StatsIcon />}
          label={`Taxa de Acomodação: ${statistics?.accommodationRate || 0}%`}
          color={statistics && statistics.accommodationRate >= 80 ? 'success' : 'warning'}
          variant="outlined"
        />
      </Box>

      <DataTable
        columns={columns}
        rows={disabilities}
        getRowId={(disability) => disability.id}
        loading={loading}
        emptyState={{
          title: 'Nenhuma deficiência encontrada',
          description: 'Cada registro descreve a deficiência de um funcionário das empresas do seu acesso.',
        }}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            onClick: handleEdit,
            ...editar,
          },
          {
            icon: <PictureAsPdfIcon fontSize="small" />,
            onClick: handleGenerateLaudo,
            ...gerarLaudo,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: (disability) => handleDeleteClick(disability.id),
            ...excluir,
          },
        ]}
        pagination={{
          page,
          rowsPerPage,
          totalCount: total,
          onPageChange: setPage,
          onRowsPerPageChange: (value) => {
            setRowsPerPage(value);
            setPage(0);
          },
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o registro de deficiência de{' '}
            <strong>{disabilityToDelete?.employeeName}</strong>? O registro será desativado.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            {deleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <DisabilitiesImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportComplete={handleImportComplete}
      />
    </PageShell>
  );
};

export default DisabilitiesListPage;
